/* XNote Write PDP S1 buy stage (docs/design-v3-light.md §4.2, §7.6; PDP r4 docs/build/pdp-r4/PLAN.md §3–§4). Loaded
   deferred by sections/xp-write-buy.liquid right after xp-write.js; no dependencies beyond xp-base.js's cart (XP.cart,
   the [data-xp-add-items] click handler). Moved out of xp-write.js by the r4 B0 cut. Every part works without it (the
   gallery swipes natively, the accordions are native, the form posts one set).
   - Gallery: native scroll-snap track (swipe), arrows, thumbnails, keyboard (← → Home End), inactive slides inert,
     videos pause when their slide leaves, the neighbours are warmed up (images eager, video posters set). A step owns
     the index until its scroll settles, and a viewport change restores the slide in view.
   - 360 slide: the R3 loop plays on tap only (a toggle button; pauses when its slide leaves).
   - Guide slide (last): the v2 gallery film; same figure, but it also plays by itself while its slide is active, the
     gallery is in view and the tab visible (not under reduced motion or Save-Data); a tap on its button pauses it.
   - Zoom: <dialog> with the large file (created and loaded only when opened), prev/next, "Actual size" (aria-pressed)
     or a click on the image switches 1:1 at the file's own width, the slide's honest label under the image. Focus
     wraps (XP.trapFocus); the count is a polite live region; Esc/close returns focus.
   - 3D (opt-in): "View in 3D" swaps the stage for snippets/xp-model.liquid and presses its loader (0 bytes before).
   - Owners slide (r4): Judge.me photos ship in data-xp-late-src(set); they load only when the slide or its left
     neighbour is active (never on the idle wake), and the thumb follows.
   - Box slide (r4): "Everything in the box ↓" opens the "In the box" accordion (#in-the-box) and scrolls to it.
   - Buy box (PDP r4, PLAN §3.2–§3.4, §3.8): quantity cards (radios xp_sets) keep the form's ONE quantity field #pdp-qty,
     the button label ("Add to cart" / "Add 2 sets to cart" / "Add 3 sets to cart"), the price row (.is-multi + the
     matching exact-total line), the card note, native accelerated-checkout add-on fields, every [data-xp-sel-label]
     and a data-xp-add-items JSON on the main button and every [data-xp-buy-mirror] (sticky bar, closing buy row): the
     set line plus each ticked, visible add-on at quantity 1, so xp-base.js posts them in ONE /cart/add.js request.
     "1 set" on every load (and whenever the deal state changes); ?sets=2 preselects 2 sets only while the deal is on; a
     hidden card is never left checked (CSS-hidden radios still submit).
   - Polish round 1: the add-on row shows on phones too (under Buy with Shop; ticking one scrolls by the height Buy with
     Shop gives up, so the row stays put), and the desktop sticky pill shows on load while Add to cart starts below a
     short screen's fold (initEarlySticky).
*/
(() => {
  'use strict';
  const d = document;
  const root = d.documentElement;
  const $ = (s, r = d) => r.querySelector(s);
  const $$ = (s, r = d) => Array.from(r.querySelectorAll(s));
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => (reduced.matches ? 'auto' : 'smooth');
  const setPoster = (v) => { if (v && v.dataset.xpPoster) { v.poster = v.dataset.xpPoster; delete v.dataset.xpPoster; } };
  const playSafe = (v) => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
  const shown = (el) => Boolean(el) && el.getClientRects().length > 0;

  /* ---------- Gallery ---------- */
  function initGallery(gal) {
    const track = $('[data-xp-write-track]', gal);
    if (!track) return null;
    const slides = $$('.xp-write-gal__slide', track);
    const thumbs = $$('[data-xp-write-go]', gal);
    const counts = $$('[data-xp-write-count]', gal);
    const prevs = $$('[data-xp-write-prev]', gal);
    const prev = prevs[0];
    const nexts = $$('[data-xp-write-next]', gal);
    const next = nexts[0];
    let index = 0;
    let raf = 0;
    // Guide slide (v2 gallery film): muted loop that plays by itself only while its slide is active, the gallery is in
    // view and the tab is visible; reduced motion and Save-Data leave the poster with its play button (a tap plays).
    const conn = navigator.connection || {};
    const still = () => reduced.matches || root.classList.contains('xp-lite') || Boolean(conn.saveData) || /2g$/.test(conn.effectiveType || '');
    const autos = $$('[data-xp-write-autoplay]', gal);
    let galSeen = true;
    const syncAuto = () => {
      autos.forEach((fig) => {
        const v = $('video', fig);
        const slide = fig.closest('.xp-write-gal__slide');
        if (!v || !slide) return;
        const active = slide.classList.contains('is-active');
        if (!active) { fig.dataset.held = ''; fig.dataset.user = ''; }
        // A tap on the play button always plays (even under reduced motion); everything else needs the calm conditions.
        const want = active && galSeen && !d.hidden && !gal.classList.contains('is-3d') && (fig.dataset.user ? true : !still() && !fig.dataset.held);
        if (want && v.paused) { setPoster(v); if (v.preload === 'none') v.preload = 'auto'; playSafe(v); }
        else if (!want && !v.paused) v.pause();
      });
    };
    if (autos.length) {
      if ('IntersectionObserver' in window) new IntersectionObserver((es) => { galSeen = es.some((e) => e.isIntersecting && e.intersectionRatio >= 0.4); syncAuto(); }, { threshold: [0, 0.4] }).observe(gal);
      d.addEventListener('visibilitychange', syncAuto);
      reduced.addEventListener('change', syncAuto);
    }

    // Slides 2+ ship their image URLs in data-xp-gsrc / data-xp-gsrcset (QA fixes T-03: as plain loading="lazy" they sat
    // inside Chrome's lazy distance and loaded before the LCP). They get them after `load` (+ an idle beat), or at once
    // on the first gallery interaction; until then only the current slide is warmed. <noscript> copies cover no-JS.
    let awake = false;
    // Polish round 2: an image with data-xp-gmedia (the page slide's phone board vs. its desktop pair) waits until its
    // media query matches, so each width downloads only the layout it shows; a later width change reveals the other.
    const media = (img) => !img.dataset.xpGmedia || matchMedia(img.dataset.xpGmedia).matches;
    const reveal = (img) => {
      if (!media(img)) return;
      if (img.dataset.xpGsrcset) { img.srcset = img.dataset.xpGsrcset; delete img.dataset.xpGsrcset; }
      if (img.dataset.xpGsrc) { img.src = img.dataset.xpGsrc; delete img.dataset.xpGsrc; }
    };
    // Judge.me photos (owners slide) wait for the slide or its left neighbour, and bring their thumbnail along.
    const late = (img) => {
      if (img.dataset.xpLateSrcset) { img.srcset = img.dataset.xpLateSrcset; delete img.dataset.xpLateSrcset; }
      if (img.dataset.xpLateSrc) { img.src = img.dataset.xpLateSrc; delete img.dataset.xpLateSrc; }
    };
    const warm = (s) => {
      if (!s) return;
      $$('img[data-xp-gsrc], img[data-xp-gsrcset]', s).forEach(reveal);
      if ($('img[data-xp-late-src]', s)) {
        $$('img[data-xp-late-src]', s).forEach(late);
        const t = thumbs[slides.indexOf(s)];
        if (t) $$('img[data-xp-late-src]', t).forEach(late);
      }
      $$('img[loading="lazy"]', s).forEach((img) => { img.loading = 'eager'; });
      $$('video', s).forEach(setPoster);
    };
    const wake = () => {
      if (awake) return;
      awake = true;
      warm(slides[index + 1]); warm(slides[index - 1]);
      $$('img[data-xp-gsrc], img[data-xp-gsrcset]', track).forEach(reveal);
    };
    matchMedia('(min-width: 768px)').addEventListener('change', () => {
      if (awake) $$('img[data-xp-gsrc], img[data-xp-gsrcset]', track).forEach(reveal);
      else warm(slides[index]);
    });
    const setActive = (i) => {
      index = i;
      slides.forEach((s, k) => {
        const on = k === i;
        s.inert = !on;
        s.classList.toggle('is-active', on);
        if (!on) $$('video', s).forEach((v) => { if (!v.paused) v.pause(); });
      });
      thumbs.forEach((t, k) => (k === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
      const strip = thumbs[i] && thumbs[i].parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        const t = thumbs[i];
        if (t.offsetLeft < strip.scrollLeft) strip.scrollLeft = t.offsetLeft - 2;
        else if (t.offsetLeft + t.offsetWidth > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = t.offsetLeft + t.offsetWidth - strip.clientWidth + 2;
      }
      counts.forEach((count) => { count.textContent = String(i + 1); });
      prevs.forEach((button) => { button.disabled = i === 0; });
      nexts.forEach((button) => { button.disabled = i === slides.length - 1; });
      warm(slides[i]);
      if (awake) { warm(slides[i + 1]); warm(slides[i - 1]); }
      syncAuto();
    };
    const current = () => Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
    let target = -1;
    let settle = 0;
    const done = () => {
      clearTimeout(settle);
      target = -1;
      const i = current();
      if (i !== index) setActive(i);
    };
    const go = (i, opts = {}) => {
      wake();
      target = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[target].offsetLeft, behavior: opts.instant ? 'auto' : behavior() });
      setActive(target);
      clearTimeout(settle);
      settle = setTimeout(done, 900);
    };
    ['pointerdown', 'focusin', 'touchstart'].forEach((ev) => gal.addEventListener(ev, wake, { once: true, passive: true }));
    const idleWake = () => setTimeout(() => ('requestIdleCallback' in window ? requestIdleCallback(wake, { timeout: 2000 }) : wake()), 1200);
    if (d.readyState === 'complete') idleWake(); else addEventListener('load', idleWake, { once: true });
    track.addEventListener('scroll', () => {
      wake();
      if (target > -1) { clearTimeout(settle); settle = setTimeout(done, 160); return; }
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => { const i = current(); if (i !== index) setActive(i); });
    }, { passive: true });
    track.addEventListener('scrollend', () => { if (target > -1 && Math.abs(track.scrollLeft - slides[target].offsetLeft) < 2) done(); });
    const restore = () => track.scrollTo({ left: slides[target > -1 ? target : index].offsetLeft, behavior: 'auto' });
    if ('ResizeObserver' in window) new ResizeObserver(restore).observe(track);
    addEventListener('resize', restore);

    const step = (by, self, other) => { go(index + by); if (self.disabled && other) other.focus({ preventScroll: true }); };
    prevs.forEach((button, i) => button.addEventListener('click', () => step(-1, button, nexts[i])));
    nexts.forEach((button, i) => button.addEventListener('click', () => step(1, button, prevs[i])));
    thumbs.forEach((t) => t.addEventListener('click', () => {
      if (gal.classList.contains('is-3d') && api.exit3d) api.exit3d(false);
      go(Number(t.dataset.xpWriteGo));
    }));
    gal.addEventListener('keydown', (e) => {
      if (gal.classList.contains('is-3d') || e.target.closest('video, input, textarea, select')) return;
      const keys = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: slides.length - 1 };
      if (!(e.key in keys) || e.altKey || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      go(keys[e.key]);
      if (e.target.closest('[data-xp-write-go]') && thumbs[index]) thumbs[index].focus();
      else if (e.target.closest('.xp-write-gal__slide')) ($('[data-xp-write-zoom-open], [data-xp-write-spin-play], video', slides[index]) || prev || track).focus({ preventScroll: true });
    });

    // 360 slide: the loop plays on tap only; its button follows the video's own state.
    $$('[data-xp-write-spin]', gal).forEach((fig) => {
      const v = $('video', fig);
      const btn = $('[data-xp-write-spin-play]', fig);
      if (!v || !btn) return;
      const sync = () => {
        const on = !v.paused && !v.ended;
        fig.classList.toggle('is-playing', on);
        btn.setAttribute('aria-pressed', String(on));
      };
      ['play', 'pause', 'ended'].forEach((ev) => v.addEventListener(ev, sync));
      v.addEventListener('playing', () => fig.classList.add('is-live'));
      btn.addEventListener('click', () => {
        setPoster(v);
        if (v.paused) { if (v.preload === 'none') v.preload = 'auto'; playSafe(v); } else v.pause();
        if (fig.hasAttribute('data-xp-write-autoplay')) { fig.dataset.held = v.paused ? '1' : ''; fig.dataset.user = v.paused ? '' : '1'; }
      });
    });

    // "Everything in the box ↓": open the accordion it points at, then scroll to it (the hash link alone would land on
    // a closed row).
    $$('[data-xp-wb-open]', gal).forEach((a) => a.addEventListener('click', (e) => {
      const target = d.getElementById(a.dataset.xpWbOpen);
      if (!target) return;
      e.preventDefault();
      if (target.tagName === 'DETAILS') target.open = true;
      target.scrollIntoView({ behavior: behavior(), block: 'start' });
      const sum = $('summary', target);
      if (sum) setTimeout(() => sum.focus({ preventScroll: true }), reduced.matches ? 0 : 400);
    }));

    setActive(0);
    const api = { go, get index() { return index; }, slides, root: gal };
    return api;
  }

  /* ---------- Zoom dialog ---------- */
  function initZoom(gal) {
    const dlg = $('[data-xp-write-zoom]');
    if (!dlg || !gal || typeof dlg.showModal !== 'function') return;
    const view = $('[data-xp-write-zoom-view]', dlg);
    const img = d.createElement('img');
    img.className = 'xp-write-zoom__img';
    img.width = 2048;
    img.height = 2048;
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;
    view.appendChild(img);
    img.addEventListener('load', () => img.style.setProperty('--xp-zoom-w', `${img.naturalWidth}px`));
    const cap = $('[data-xp-write-zoom-cap]', dlg);
    const countEl = $('[data-xp-write-zoom-count]', dlg);
    const zoomable = gal.slides.filter((s) => s.dataset.zoom);
    const actual = $('[data-xp-write-zoom-actual]', dlg);
    let at = 0;
    let opener = null;
    if (window.XP && XP.trapFocus) XP.trapFocus(dlg);
    const setZoomed = (on) => {
      view.classList.toggle('is-zoomed', on);
      if (actual) {
        actual.setAttribute('aria-pressed', String(on));
        actual.setAttribute('aria-label', on ? 'Görseli ekrana sığdır' : 'Yakınlaştır');
      }
      const hint = $('[data-xp-write-zoom-hint]', dlg);
      if (hint) hint.textContent = on ? 'İncelemek için sürükleyin · Sığdırmak için dokunun' : 'Gezinmek için kaydırın · Yakınlaştırmak için dokunun';
      if (on) { view.tabIndex = 0; view.setAttribute('role', 'region'); view.setAttribute('aria-label', 'Yakınlaştırılmış görsel'); }
      else { view.removeAttribute('tabindex'); view.removeAttribute('role'); view.removeAttribute('aria-label'); }
    };
    const label = (s) => $$('.xp-tag', s).filter((n) => n.getClientRects().length > 0).map((n) => n.textContent.trim()).join(' ');
    const show = (k) => {
      at = (k + zoomable.length) % zoomable.length;
      const s = zoomable[at];
      const src = $('img', s);
      const n = at + 1;
      if (view.contains(d.activeElement) || d.activeElement === view) (actual || $('[data-xp-write-zoom-close]', dlg)).focus({ preventScroll: true });
      setZoomed(false);
      view.scrollTo(0, 0);
      img.style.removeProperty('--xp-zoom-w');
      img.removeAttribute('src');
      img.alt = src ? src.alt : '';
      img.src = s.dataset.zoom;
      cap.textContent = label(s);
      const vis = d.createElement('span');
      vis.setAttribute('aria-hidden', 'true');
      vis.textContent = `${n} / ${zoomable.length}`;
      const said = d.createElement('span');
      said.className = 'xp-vh';
      said.textContent = `Görsel ${n} / ${zoomable.length}${img.alt ? `: ${img.alt}` : ''}`;
      countEl.replaceChildren(vis, said);
      gal.go(gal.slides.indexOf(s), { instant: true });
    };
    const toggle = (e) => {
      const zoomed = !view.classList.contains('is-zoomed');
      const r = img.getBoundingClientRect();
      const fx = e && e.clientX ? (e.clientX - r.left) / r.width : 0.5;
      const fy = e && e.clientY ? (e.clientY - r.top) / r.height : 0.5;
      if (!zoomed && d.activeElement === view && actual) actual.focus({ preventScroll: true });
      setZoomed(zoomed);
      if (zoomed) requestAnimationFrame(() => view.scrollTo({ left: img.offsetWidth * fx - view.clientWidth / 2, top: img.offsetHeight * fy - view.clientHeight / 2 }));
    };
    d.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-xp-write-zoom-open]');
      if (!btn) return;
      opener = btn;
      show(zoomable.indexOf(btn.closest('.xp-write-gal__slide')));
      root.classList.add('xp-locked');
      dlg.showModal();
      $('[data-xp-write-zoom-close]', dlg).focus();
    });
    $('[data-xp-write-zoom-prev]', dlg).addEventListener('click', () => show(at - 1));
    $('[data-xp-write-zoom-next]', dlg).addEventListener('click', () => show(at + 1));
    $('[data-xp-write-zoom-close]', dlg).addEventListener('click', () => dlg.close());
    // Swipe only when fitted: zoomed images retain native panning and pinch gestures.
    let gesture = null;
    let suppressTapUntil = 0;
    view.addEventListener('pointerdown', (e) => {
      if (!e.isPrimary) { gesture = null; return; }
      if ((e.pointerType === 'mouse' && e.button !== 0) || view.classList.contains('is-zoomed')) return;
      gesture = { id: e.pointerId, x: e.clientX, y: e.clientY, time: performance.now() };
    }, { passive: true });
    view.addEventListener('pointerup', (e) => {
      if (!gesture || e.pointerId !== gesture.id) return;
      const dx = e.clientX - gesture.x;
      const dy = e.clientY - gesture.y;
      const elapsed = performance.now() - gesture.time;
      gesture = null;
      if (Math.max(Math.abs(dx), Math.abs(dy)) > 12) suppressTapUntil = performance.now() + 450;
      if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.25 && elapsed < 1200) show(at + (dx < 0 ? 1 : -1));
    }, { passive: true });
    view.addEventListener('pointercancel', () => { gesture = null; suppressTapUntil = performance.now() + 450; });
    img.addEventListener('click', (e) => { if (performance.now() >= suppressTapUntil) toggle(e); });
    if (actual) actual.addEventListener('click', () => toggle());
    dlg.addEventListener('keydown', (e) => {
      if (view.classList.contains('is-zoomed') && e.target === view) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(at - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(at + 1); }
    });
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    dlg.addEventListener('close', () => {
      root.classList.remove('xp-locked');
      setZoomed(false);
      const s = zoomable[at];
      const back = (s && $('[data-xp-write-zoom-open]', s)) || opener;
      if (back) back.focus({ preventScroll: true });
    });
  }

  /* ---------- 3D toggle (opt-in; snippets/xp-model.liquid) ---------- */
  function init3d(gal) {
    if (!gal) return;
    const g = gal.root;
    const panel = $('[data-xp-write-3d-panel]', g);
    const toggle = $('[data-xp-write-3d]', g);
    const model = panel && $('[data-xp-model]', panel);
    if (!panel || !toggle || !model) return;
    let active = false;
    let leaving = false;
    const enter = () => {
      active = true;
      g.classList.add('is-3d');
      panel.hidden = false;
      toggle.setAttribute('aria-pressed', 'true');
      $$('.xp-write-gal__slide video', g).forEach((v) => v.pause());
      const open = $('[data-xp-model-open]', model);
      if ((model.dataset.state === 'poster' || model.dataset.state === 'error') && open) open.click();
    };
    const exit = (focusToggle = true) => {
      if (!active || leaving) return;
      leaving = true;
      const close = $('[data-xp-model-action="close"]', model);
      if (close && model.dataset.state === 'ready') close.click();
      active = false;
      g.classList.remove('is-3d');
      panel.hidden = true;
      toggle.setAttribute('aria-pressed', 'false');
      if (focusToggle) toggle.focus({ preventScroll: true });
      leaving = false;
    };
    toggle.addEventListener('click', () => (active ? exit(true) : enter()));
    let last = model.dataset.state;
    new MutationObserver(() => {
      const state = model.dataset.state;
      if (active && last === 'ready' && state === 'poster') exit(true);
      last = state;
    }).observe(model, { attributes: true, attributeFilter: ['data-state'] });
    gal.exit3d = exit;
  }

  /* ---------- Buy box: quantity cards, add-ons, one add request (PDP r4, PLAN §3.2–§3.4) ---------- */
  function initBuy() {
    const form = $('#pdp-form');
    const box = $('.xp-write-box');
    const add = $('[data-xp-wb-add]');
    const cards = $('[data-xp-wb-cards]');
    if (!form || !box || !add || !cards) return;
    form.removeAttribute('data-xp-add-form'); // the Ajax add runs from data-xp-add-items (one request, add-ons included)
    const qty = $('#pdp-qty');
    const idInput = $('input[name="id"]', form);
    const setId = Number(idInput && idInput.value);
    const radios = $$('input[name="xp_sets"]', cards);
    const one = radios.find((r) => r.value === '1');
    const addonBox = $('[data-xp-wb-addons]', form);
    const addons = addonBox ? $$('[data-xp-wb-addon]', addonBox) : [];
    const notes = $('.xp-write-tiers__notes', form);
    const note = (k) => $(`[data-xp-wb-note="${k}"]`, form);
    const prices = $$('[data-xp-wb-price]', box);
    const three = cards.dataset.xpThree || 'pair_plus_one';
    const hasDeal = cards.hasAttribute('data-xp-deal-markup');
    const dealOn = () => hasDeal && root.getAttribute('data-xp-deal') === 'on';
    const visibleRadio = (r) => Boolean(r) && shown(r.closest('label'));
    const selected = () => { const r = radios.find((x) => x.checked); return visibleRadio(r) ? r : one; };

    const items = () => {
      const r = selected();
      const n = Number(r && r.value) || 1;
      const lines = [{ id: setId, quantity: n }];
      if (addonBox && shown(addonBox)) addons.forEach((c) => { if (c.checked) lines.push({ id: Number(c.value), quantity: 1 }); });
      return lines;
    };
    const sync = () => {
      const r = selected();
      if (r && !r.checked) r.checked = true;
      const n = Number(r && r.value) || 1;
      const deal = dealOn();
      if (qty) qty.value = String(n);
      box.dataset.sets = String(n);
      add.textContent = n > 1 ? `Sepete ${n} Set Ekle` : 'Sepete Ekle';
      // Price row: the exact-total line for this count, if one shows in the current deal state.
      let multi = null;
      prices.forEach((p) => {
        p.classList.remove('is-on');
        if (!multi && n > 1 && p.dataset.xpWbPrice === String(n) && (p.getAttribute('data-deal-only') === 'on' ? deal : !(deal && p.getAttribute('data-deal-hide') === 'on'))) multi = p;
      });
      if (multi) multi.classList.add('is-on');
      box.classList.toggle('is-multi', Boolean(multi));
      // Card note (PLAN §3.2 table).
      const dealNote = deal && (n === 2 || (n === 3 && three === 'pair_plus_one'));
      const tiersNote = (!deal && n > 1) || (deal && n === 3 && three === 'tier');
      if (note('tiers')) note('tiers').hidden = !tiersNote;
      if (note('one')) note('one').hidden = !(deal && n === 1);
      if (note('pair')) note('pair').hidden = !dealNote;
      if (notes) notes.classList.toggle('is-empty', !$$('p', notes).some((p) => !p.hidden && shown(p)));
      const lines = items();
      // Shopify's native accelerated checkout reads additional variant fields from this product form.
      // Rebuild only our fields so deselection cannot leave a stale add-on in checkout.
      $$('[data-xp-accelerated-addon]', form).forEach((input) => input.remove());
      const accelerated = document.createDocumentFragment();
      lines.slice(1).forEach((line, index) => {
        ['id', 'quantity'].forEach((key) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `shopify-additional-items[${index + 1}][${key}]`;
          input.value = String(line[key]);
          input.dataset.xpAcceleratedAddon = '';
          accelerated.appendChild(input);
        });
      });
      form.appendChild(accelerated);
      const json = JSON.stringify(lines);
      add.dataset.xpAddItems = json;
      $$('[data-xp-buy-mirror]').forEach((m) => { m.dataset.xpAddItems = json; });
      const sel = (r && (deal ? r.dataset.selDeal : r.dataset.selReg)) || '';
      if (sel) $$('[data-xp-sel-label]').forEach((el) => { el.textContent = sel; });
      if (r && !deal) $$('[data-xp-sel-was]').forEach((el) => { el.textContent = r.dataset.selWas || ''; el.hidden = !r.dataset.selWas; });
    };
    // Never leave a hidden card checked; a deal switch (start or end instant) re-selects "1 set": the cards' prices
    // changed under the visitor, so keeping 2 or 3 sets would be a preselection they didn't make at that price.
    let lastDeal = dealOn();
    const reconcile = () => {
      const now = dealOn();
      if (now !== lastDeal) { lastDeal = now; if (one) one.checked = true; }
      const r = radios.find((x) => x.checked);
      if (!visibleRadio(r) && one) one.checked = true;
      sync();
    };
    // Unticked and "1 set" on every load, including a back/forward restore.
    const reset = () => { if (one) one.checked = true; addons.forEach((c) => { c.checked = false; }); };
    reset();
    cards.addEventListener('change', sync);
    // Keep the mobile add-on row under the finger if selection changes the surrounding layout.
    if (addonBox) addonBox.addEventListener('change', () => {
      const y0 = addonBox.getBoundingClientRect().top;
      sync();
      const dy = addonBox.getBoundingClientRect().top - y0;
      if (Math.abs(dy) > 1) window.scrollBy({ top: dy, left: 0, behavior: 'instant' });
    });
    // ?sets=2 preselects 2 sets, only while the deal is on (the deal page's CTA may link here).
    const sets = new URLSearchParams(location.search).get('sets');
    if (sets === '2' && dealOn()) {
      const two = radios.find((x) => x.value === '2');
      if (visibleRadio(two)) two.checked = true;
    }
    reconcile();
    new MutationObserver(reconcile).observe(root, { attributes: true, attributeFilter: ['data-xp-deal'] });
    d.addEventListener('xp:deal', reconcile);
    addEventListener('pageshow', (e) => { if (e.persisted) { reset(); sync(); } });
    // The sticky bar is moved into the layout's host by xp-base.js, and SECTIONS' closing row may render later: label
    // the mirrors again once everything has loaded, and once the page settles.
    addEventListener('load', sync, { once: true });
    addEventListener('resize', () => { clearTimeout(sync._t); sync._t = setTimeout(sync, 150); });
  }

  /* ---------- Sticky bar before the first scroll (polish round 1; phones since polish round 2) ----------
     xp-base.js shows the bar once the main Add to cart has scrolled out above the screen. On a short screen the button
     can start BELOW the first screen, so nothing to buy would be in view: while the button is still below the fold,
     the bar shows (the desktop pill ≥ 768 px, the bottom bar on phones: 375 × 667 and 320 × 568 end at the price row).
     Every other state stays xp-base's: when the button scrolls into view its observer hides the bar, and this only
     steps in again once the button is back below the fold. */
  function initEarlySticky() {
    const bar = $('[data-xp-sticky]');
    const add = $('[data-xp-wb-add]');
    if (!bar || !add || !('IntersectionObserver' in window)) return;
    const wide = matchMedia('(min-width: 768px)');
    const fits = () => !wide.matches || bar.classList.contains('xp-sticky--desktop');
    const below = () => { const r = add.getBoundingClientRect(); return r.height > 0 && r.top >= innerHeight - 4; };
    const show = () => {
      bar.classList.add('is-visible');
      bar.inert = false;
      bar.setAttribute('aria-hidden', 'false');
      root.classList.add('xp-sticky-on');
      if (bar.hasAttribute('data-xp-sticky-hides-header-cta')) root.classList.add('xp-sticky-hides-cta');
    };
    const check = () => { if (fits() && !bar.hidden && !bar.classList.contains('is-visible') && below()) show(); };
    d.addEventListener('xp:sticky:change', (e) => { if (!(e.detail && e.detail.visible)) check(); });
    const later = () => setTimeout(check, 60);
    if (d.readyState === 'complete') later(); else addEventListener('load', later, { once: true });
    if (d.fonts && d.fonts.ready) d.fonts.ready.then(later);
  }

  const start = () => {
    const galRoot = $('[data-xp-write-gallery]');
    const gal = galRoot ? initGallery(galRoot) : null;
    initZoom(gal);
    init3d(gal);
    initBuy();
    initEarlySticky();
  };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start); else start();
})();
